import { Metadata } from "@dropbox/dash-component-library";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { generateSearchURL } from "@mirage/search/hooks/useQueryParams";
import { openURL } from "@mirage/service-platform-actions";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { KeyCodes } from "@mirage/shared/util/constants";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { ChatSource } from "../../types";
import { getSourceRowParts } from "../compose-sources/ComposeSourceRow";
import styles from "./AllSourcesTiles.module.css";

// We're only displaying the top few results for now.
// user has to click "See all" to get the rest
const MAX_DISPLAYED_SOURCES = 3;

export const AllSourcesTiles = ({
  sources,
  query,
}: {
  sources: ChatSource[];
  query?: string;
}) => {
  const navigate = useNavigate();
  const isMobileSize = useIsMobileSize();
  const truncatedSourcesCount = sources.length - MAX_DISPLAYED_SOURCES;

  const handleSeeAllResults = useCallback(() => {
    navigate(generateSearchURL(query ?? ""));
  }, [query, navigate]);
  return (
    <div
      className={classNames(styles.container, isMobileSize && styles.isMobile)}
    >
      {sources.slice(0, MAX_DISPLAYED_SOURCES).map((source, i) => {
        const sourceRowParts = getSourceRowParts(source, {
          hideBackground: true,
          icon: {
            size: "small",
          },
        });
        const url = sourceRowParts?.mirageResult.url;
        if (!sourceRowParts || !url) {
          return null;
        }

        const handleSourceClick = () => {
          if (source.type !== "local_file_content") {
            openURL(url);
          }
        };

        return (
          <div
            key={i}
            role="button"
            tabIndex={0}
            className={classNames(
              styles.source,
              isMobileSize && styles.isMobile,
            )}
            onClick={handleSourceClick}
            onKeyDown={(e) => {
              if (e.key === KeyCodes.enter || e.key === KeyCodes.space) {
                handleSourceClick();
              }
            }}
          >
            <Text variant="label">
              <Truncate lines={2}>{sourceRowParts?.titleString}</Truncate>
            </Text>
            <Metadata size="small">
              <Metadata.Item>
                <div className={styles.sourceIcon}>{sourceRowParts?.icon}</div>
                <Metadata.Label>
                  {sourceRowParts?.sizeOrMtimeString}
                </Metadata.Label>
              </Metadata.Item>
            </Metadata>
          </div>
        );
      })}
      {truncatedSourcesCount > 0 && (
        <div
          className={classNames(styles.source, isMobileSize && styles.isMobile)}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (query) {
              handleSeeAllResults();
            }
          }}
          onKeyDown={(e) => {
            if (
              (e.key === KeyCodes.enter || e.key === KeyCodes.space) &&
              query
            ) {
              handleSeeAllResults();
            }
          }}
        >
          <Text variant="label">
            {i18n.t("see_more_count", { count: truncatedSourcesCount })}
          </Text>
          <Metadata size="small">
            <Metadata.Item>
              {sources
                .slice(MAX_DISPLAYED_SOURCES, MAX_DISPLAYED_SOURCES + 3)
                .map((source, i) => {
                  const sourceRowParts = getSourceRowParts(source, {
                    hideBackground: true,
                    icon: {
                      size: "small",
                    },
                  });
                  if (!sourceRowParts) {
                    return null;
                  }
                  return (
                    <div key={i} className={styles.sourceIcon}>
                      {sourceRowParts.icon}
                    </div>
                  );
                })}
            </Metadata.Item>
          </Metadata>
        </div>
      )}
    </div>
  );
};
